/* =====================================================================
   ZEYVO v3 — GSAP animation layer (Tier 1)
   Loaded after gsap.min.js + ScrollTrigger.min.js from layout/theme.liquid.

   Scope on purpose:
     GSAP owns  -> hero entrance timeline, stat counters, desktop parallax,
                   scroll reveals, gradient shimmer, breathing CTA glow.
     CSS owns   -> card/tile hovers, marquee scroll, kicker dot pulse.
   Nothing here animates a property that zeyvo-v3.css also animates, so the
   two layers can't fight each other.
   ===================================================================== */
(function () {
  'use strict';

  if (!window.gsap) return;
  gsap.registerPlugin(ScrollTrigger);

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP = window.matchMedia('(min-width: 750px)').matches;

  /* Elements start hidden via .zv-js .zv-anim in zeyvo-anim.css so nothing
     flashes before GSAP runs. reveal() clears that safely. */
  function reveal(targets, vars) {
    var t = gsap.utils.toArray(targets);
    if (!t.length) return null;
    return gsap.to(t, Object.assign({ opacity: 1, y: 0, clearProps: 'opacity,transform' }, vars));
  }

  function hide(targets, vars) {
    var t = gsap.utils.toArray(targets);
    if (!t.length) return [];
    gsap.set(t, vars);
    return t;
  }

  /* ---------- reduced motion: show everything, animate nothing ---------- */
  if (REDUCE) {
    document.documentElement.classList.remove('zv-js');
    return;
  }

  /* ---------- split the hero headline into words (keeps .zv-hero__grad) ---------- */
  function splitWords(root) {
    if (!root) return [];
    var out = [];
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var parts = n.textContent.split(/(\s+)/);
          if (!n.textContent.trim()) return;
          var frag = document.createDocumentFragment();
          parts.forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            var s = document.createElement('span');
            s.className = 'zv-word';
            s.textContent = p;
            frag.appendChild(s);
            out.push(s);
          });
          n.parentNode.replaceChild(frag, n);
        } else if (n.nodeType === 1) {
          /* Never split inside a background-clip:text element — child spans
             don't inherit the clip, so the letters would render invisible.
             Animate the whole gradient word as one unit instead. */
          if (n.classList && n.classList.contains('zv-hero__grad')) {
            n.classList.add('zv-word');
            out.push(n);
            return;
          }
          walk(n);
        }
      });
    })(root);
    return out;
  }

  var heading = document.querySelector('.zv-hero__heading');
  var words = splitWords(heading);

  /* ---------- 1. HERO ENTRANCE TIMELINE ---------- */
  hide('.zv-hero__kicker', { opacity: 0, y: 26 });
  hide(words, { opacity: 0, yPercent: 105 });
  hide('.zv-hero__sub', { opacity: 0, y: 24 });
  hide('.zv-hero__ctas .button', { opacity: 0, y: 24 });
  hide('.zv-hero__stats .zv-stat', { opacity: 0, y: 20 });

  var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

  tl.to('.zv-hero__kicker', { opacity: 1, y: 0, duration: 0.6, clearProps: 'transform' });

  if (words.length) {
    tl.to(words, {
      opacity: 1, yPercent: 0, duration: 0.8, stagger: 0.07,
      ease: 'power4.out', clearProps: 'transform'
    }, '-=0.3');
  }

  tl.to('.zv-hero__sub', { opacity: 1, y: 0, duration: 0.7, clearProps: 'transform' }, '-=0.45')
    .to('.zv-hero__ctas .button', { opacity: 1, y: 0, duration: 0.6, stagger: 0.12, clearProps: 'transform' }, '-=0.4')
    .to('.zv-hero__stats .zv-stat', { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, clearProps: 'transform' }, '-=0.35');

  /* hero image settles in from a slight scale-up */
  var heroImg = document.querySelector('.zv-hero__img');
  if (heroImg) tl.from(heroImg, { scale: 1.1, duration: 2.2, ease: 'power2.out' }, 0);

  /* ---------- 2. STAT COUNTERS (₹0→₹499, 00→04, 0%→100%) ----------
     Plain count-up, no scale growth. */
  gsap.utils.toArray('.zv-stat__num').forEach(function (el) {
    var raw = el.textContent.trim();
    var m = raw.match(/^(\D*)(\d[\d,]*)(\D*)$/);
    if (!m) return;
    var prefix = m[1] || '';
    var digits = m[2].replace(/,/g, '');
    var suffix = m[3] || '';
    var target = parseInt(digits, 10);
    if (!target || target > 100000) return;
    var pad = digits.length > 1 && digits.charAt(0) === '0' ? digits.length : 0;
    var obj = { v: 0 };

    el.textContent = prefix + (pad ? '0'.repeat(pad) : '0') + suffix;

    gsap.to(obj, {
      v: target, duration: 1.6, ease: 'power2.out', delay: 1.3,
      onUpdate: function () {
        var n = String(Math.round(obj.v));
        if (pad) n = n.padStart(pad, '0');
        el.textContent = prefix + n + suffix;
      },
      onComplete: function () { el.textContent = raw; }
    });
  });

  /* ---------- 3. GRADIENT SHIMMER (headline accent + section headings) ---------- */
  var shimmer = gsap.utils.toArray('.zv-hero__grad, .zv-tiles__heading, .collection .title, .related-products .title');
  if (shimmer.length) {
    gsap.to(shimmer, { backgroundPosition: '-250% 0', duration: 5, repeat: -1, ease: 'none' });
  }

  /* ---------- 4. BREATHING GLOW ON PRIMARY CTA ---------- */
  var cta = document.querySelector('.zv-hero__ctas .button:not(.button--secondary)');
  if (cta) {
    gsap.to(cta, {
      boxShadow: '0 0 42px rgba(124,58,237,0.8), 0 0 70px rgba(244,63,94,0.35)',
      duration: 1.4, repeat: -1, yoyo: true, ease: 'sine.inOut'
    });
  }

  /* ---------- 5. PARALLAX — desktop only, scroll-scrubbed ---------- */
  if (DESKTOP && heroImg) {
    gsap.to(heroImg, {
      yPercent: 8, ease: 'none',
      scrollTrigger: { trigger: '.zv-hero', start: 'top top', end: 'bottom top', scrub: true }
    });
  }

  /* ---------- 6. SCROLL REVEALS ---------- */
  function onScroll(targets, from, extra) {
    gsap.utils.toArray(targets).forEach(function (el) {
      gsap.set(el, from);
      gsap.to(el, Object.assign({
        opacity: 1, x: 0, y: 0, scale: 1, duration: 0.75, ease: 'power3.out',
        clearProps: 'opacity,transform',
        scrollTrigger: { trigger: el, start: 'top 88%' }
      }, extra || {}));
    });
  }

  /* section headings */
  onScroll('.zv-tiles__heading, .collection .title, .related-products .title, .zeyvo-footer h2',
    { opacity: 0, y: 30 });

  /* collection tiles — staggered as a group */
  var tiles = gsap.utils.toArray('.zv-tile');
  if (tiles.length) {
    gsap.set(tiles, { opacity: 0, y: 60 });
    gsap.to(tiles, {
      opacity: 1, y: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out',
      clearProps: 'opacity,transform',
      scrollTrigger: { trigger: '.zv-tiles__grid', start: 'top 85%' }
    });
  }

  /* product cards — staggered per grid */
  gsap.utils.toArray('.product-grid, .grid--4-col-desktop, .collection .grid').forEach(function (grid) {
    var items = grid.querySelectorAll('.grid__item');
    if (!items.length) return;
    gsap.set(items, { opacity: 0, y: 50, scale: 0.97 });
    gsap.to(items, {
      opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.08, ease: 'power3.out',
      clearProps: 'opacity,transform',
      scrollTrigger: { trigger: grid, start: 'top 88%' }
    });
  });

  /* No curtain/clip-path reveal on product photos: with immediateRender:false
     the image paints visible first and then gets re-hidden, which reads as a
     flash. Hiding it upfront instead risks permanently invisible products.
     The card stagger below already gives the grid its entrance. */

  /* trust row items fly in from the left */
  var trust = gsap.utils.toArray('.zeyvo-trust-item');
  if (trust.length) {
    gsap.set(trust, { opacity: 0, x: -22 });
    gsap.to(trust, {
      opacity: 1, x: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out',
      clearProps: 'opacity,transform',
      scrollTrigger: { trigger: '.zeyvo-trust-grid', start: 'top 90%' }
    });
  }

  /* trust icons: gentle glow pulse */
  var ticons = gsap.utils.toArray('.zeyvo-trust-item svg');
  if (ticons.length) {
    gsap.to(ticons, {
      filter: 'drop-shadow(0 0 7px rgba(167,139,250,0.95))',
      duration: 1.5, repeat: -1, yoyo: true, ease: 'sine.inOut', stagger: 0.3
    });
  }

  /* footer */
  onScroll('.zeyvo-footer > *, .footer__content-top > *', { opacity: 0, y: 24 },
    { duration: 0.7, stagger: 0.1 });

  /* Shopify theme editor: recalc triggers when sections re-render */
  document.addEventListener('shopify:section:load', function () { ScrollTrigger.refresh(); });
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
})();
